import { FAQItemProps } from '@zola/zola-ui/src/components/PreAuthV3/FAQItem';
import { Fragment } from 'react';
import styled from '@emotion/styled';
import { SPACING } from '@zola/zola-ui/src/styles/emotion';

const UnorderedList = styled.ul`
  list-style-position: outside;
  list-style-type: disc;
  padding-inline-start: ${SPACING.MD};
`;

const OrderedList = styled.ol`
  list-style-position: outside;
  list-style-type: decimal;
  padding-inline-start: 25px;
`;

export const FAQItems: FAQItemProps[] = [
  {
    answer: (
      <Fragment>
        From the pre-wedding couple poses to pictures of your niece’s cake-covered face, your
        wedding photographer captures countless moments on your big day. A wedding album brings all
        your favorite memories together into a beautiful keepsake you and your loved ones will
        cherish for generations to come.
        <br />
        <br />
        At Zola, our free Album Wizard tool takes care of all the heavy lifting — like helping you
        pick, organize, and lay out your favorite photos beautifully — so you can focus on the fun
        part: customizing an album fit for this special milestone in your lives. It’s the easiest
        way to create a home for your favorite wedding memories that you can relive time and time
        again.
      </Fragment>
    ),
    question: 'Why do we need a wedding photo album?',
  },
  {
    answer: (
      <Fragment>
        Our photo albums are made with luxe, high-quality paper in a variety of thicknesses and
        shine levels from Semi-Matte to Soft Gloss, so your wedding album will have a feel that’s
        just right for you.
        <br />
        <br />
        Customize your album cover with the most beautiful essentials and upgrades, including:
        <UnorderedList>
          <li>Linen</li>
          <li>Vegan leather</li>
          <li>Italian leather</li>
          <li>Colored foil</li>
          <li>And more!</li>
        </UnorderedList>
        <br />
        All our albums are printed using photographic printing, a special technique that uses
        chemical reactions and light exposure to produce crystal-clear photos. This premium printing
        method — a go-to for professional photographers — brings photos to life with rich color,
        captivating image depth, and stunning contrasts. You won’t find this superior quality of
        materials and printing, at these prices, anywhere else.
      </Fragment>
    ),
    question: 'What are Zola’s wedding photo albums made with?',
  },
  {
    answer: (
      <Fragment>
        There are many benefits of creating your own wedding album online with Zola, including:
        <UnorderedList>
          <li>
            <b>Creative control</b> – You don&apos;t need a professional photographer to design your
            album. You&apos;re the creative director of this project. By creating your own custom
            wedding album, you can guarantee that your favorite moments are captured exactly where
            you want them. Plus, you can add your own style and flair to every page.{' '}
          </li>
          <li>
            <b>It’s cost-effective</b> – If your wedding photographer creates your wedding album,
            you will pay for hours, equipment, and materials — and that can rack up quite a bill.
            Doing it yourself allows you much more flexibility and helps save you money.{' '}
          </li>
          <li>
            <b>It doesn’t take long</b> – Our free Album Wizard tool takes care of all the heavy
            lifting for you, from picking the perfect photos to organizing them and laying them out
            beautifully. Then you can customize as much as you want. You’ll save tons of time and
            take home a beautiful album designed by wedding experts.
          </li>
          <li>
            <b>It’s so much fun!</b> – Building the perfect wedding album with your new spouse is
            half art project, half stroll down memory lane. Feel free to pop some champagne, order
            some takeout, and go through the images together and reminisce over your first married
            memories.
          </li>
        </UnorderedList>
      </Fragment>
    ),
    question:
      'What are the benefits of creating our wedding album online vs. paying wedding photographers to do it?',
  },
  {
    answer:
      'Yes! Zola offers a variety of beautiful album templates specifically designed for weddings, by wedding experts. We can help you find the wedding album template that suits your style and layout density — that is, how many photos per album and per page. You can also add your own personal touch to these templates when you customize your album.',
    question: 'Does Zola provide wedding album templates?',
  },
  {
    answer:
      'Whatever you want! When it comes to your wedding album, the only thing you “should” do is make yourself and your partner happy. Whether that means including photos from your engagement session, ceremony, or honeymoon, the perfect wedding album is one that feels uniquely yours. But if you need some advice, Zola’s Album Wizard is there for you every step of the way, with sage advice and guidance on which photos to use and how many you need.',
    question: 'What should we include in our wedding album?',
  },
  {
    answer: (
      <Fragment>
        Zola is the only place where you can create a layflat photo album designed by wedding
        experts, no heavy lifting required. Whether you’re looking to create an heirloom wedding
        album or a coffee table keepsake, our Album Wizard listens to what you’re looking for, then
        helps you pick the perfect photos and layout. You can go with its expert suggestions, or
        play around some more for a stunning keepsake that’s totally your own.
        <br />
        We also found smart ways for you to create world-class albums without emptying your pockets.
        At Zola, you can customize with the finest essentials and upgrades — from thick, luxe paper
        to beautiful foils, linens, leathers, and more — and enjoy professional photographic
        printing with all our layflat albums. You won’t find superb quality at this price anywhere
        else.
        <br />
        On your wedding day, you probably wished the joy, excitement, and beauty of the day could
        last forever (but if it did, there wouldn’t be time for the honeymoon!). Zola’s wedding
        photo albums are the perfect way to commemorate your special day forever.
      </Fragment>
    ),
    question: 'Why should we create our wedding album at Zola?',
  },
  {
    answer: (
      <Fragment>
        It’s easy! All you need to get started are your beautiful wedding photos. Our Album Wizard
        will listen to your vision, then do all the heavy lifting for you. Here’s how it works:
        <OrderedList>
          <li>
            Tell our Album Wizard what <b>size, cover, color,</b> and <b>paper</b> you’d like.
          </li>
          <li>
            Pick a <b>layout style</b>, so the Wizard knows how to best arrange your photos. You’ll
            be able to edit its suggestions or try other styles later.
          </li>
          <li>
            <b>Upload</b> your wedding photos. The Wizard will help you narrow it down and ensure
            you include the little sweet moments as well as the big ones!
          </li>
          <li>
            <b>Organize</b> your photos in minutes with the Wizard’s magic touch. Drag and drop
            categories onto your images to group them by theme (like Scenery, Getting Ready, Family,
            Ceremony, and more). You can move or hide images in bulk anytime. Worried you’ll miss
            something? Don’t sweat it. The Wizard will share expert suggestions on how many photos
            to include per category. Saves SO much time!
          </li>
          <li>
            Star your favorite photos to <b>highlight</b> them as full-page images in your album.
          </li>
          <li>
            Sit back and relax as our Wizard lays out all your photos beautifully. Then{' '}
            <b>customize</b> your album as much as you want! You can edit any layout, try other
            layout styles, change your album design, and play around with photo arrangement..
          </li>
          <li>
            Our Wizard will automatically <b>track your progress</b> as you go, so you can figure
            out which categories you still need to work on and which are ready for final proofing.
            It’ll also share sage advice and make sure you don’t make any mistakes, like leaving a
            spot empty in your album, printing the same photo twice, and more.
          </li>
          <li>
            Once you’ve created the wedding album of your dreams, carefully <b>review</b> all your
            work and place your order with peace of mind. Ta-da!
          </li>
        </OrderedList>
      </Fragment>
    ),
    question: 'How do we create a wedding album at Zola?',
  },
  {
    answer: (
      <Fragment>
        Can “photo book” and “photo album” be used interchangeably? Nope! Here are their key
        differences:
        <UnorderedList>
          <li>
            <b>Page Quality</b> – Photo books are typically printed on the same kind of paper you’d
            find in your office copy machine, made of inexpensive materials for mass production.
            Photo albums, however, are printed on photo-grade paper, which results in higher-quality
            photos and more gorgeous images. Photography-grade paper is also far more precise when
            it comes to imprinting images so you can enjoy sharper colors and dynamic images for
            many anniversaries to come.
          </li>
          <li>
            <b>Cover Options</b> – They say don’t judge a book by its cover, but your wedding photos
            deserve to wow from cover-to-cover. That’s why Zola’s wedding albums are available in a
            variety of beautiful materials such as elegant linen and rich, supple Italian leather,
            with the choice of three shimmering foil colors for your cover text. Wedding photo book
            covers are typically printed on cardboard and other lower-quality materials.
          </li>
          <li>
            <b>Print Method</b> – If you ever took a photography class in college or high school,
            you know the breathtaking effects of developing and printing photos through chemical
            reactions and light exposure. Wedding photo albums are printed using this professional
            method, resulting in rich tones and sharp picture quality. Photo books, on the other
            hand, are printed on the same presses used for mass-production which reduces the overall
            quality of some of your most precious, captured moments.
          </li>
          <li>
            <b>Expense</b> – Since wedding photo albums require extensive expertise, labor, and the
            highest quality materials, they have a reputation for being quite expensive. However,
            Zola&apos;s found smart ways for you to create stunning albums without emptying your
            wallet. That means you get to take home a beautiful keepsake you and your family can
            cherish for decades to come... without paying the steep prices on other sites.
          </li>
        </UnorderedList>
      </Fragment>
    ),
    question: `What's the difference between a "Photo Album" and a "Photo Book"?`,
  },
  {
    answer:
      'Just your wedding photos! From there, Zola’s Album Wizard can help you narrow it down, choose photos to cover every moment of your special day, and create the exact type of wedding photo album you want',
    question: 'What do we need to get started?',
  },
  {
    answer:
      'Typically, most wedding albums are about 30-40 pages. However, we believe that just like your wedding day, you can make your wedding album length your own. That’s why Zola’s wedding photo albums can be between 20-90 pages, so you can fit in every beautiful moment.',
    question: 'How many pages is a typical album?',
  },
  {
    answer:
      'Our recommendation? As soon as you get back from your honeymoon, or even on your honeymoon! The fresher your memories are, the more thought and flair you can put into the album.',
    question: 'When should we start our photo album?',
  },
  {
    answer: (
      <Fragment>
        It will take about 5-8 business days to process your wedding masterpiece — that includes
        printing, assembling the book, and preparing it for shipment. Please see our delivery
        estimates for wedding albums below, based on the shipping method you choose at checkout:
        <UnorderedList>
          <li>Standard shipping: Estimated delivery in 14 business days</li>
          <li>Expedited shipping: Estimated delivery in 11 business days</li>
        </UnorderedList>
      </Fragment>
    ),
    question: 'How long does it take to print and ship our photo album?',
  },
];
